import { APP_CONSTANTS } from 'utils/constants'
import { groupBy, whenIsDate, getNoBidClientStyle } from 'utils/helpers'
import moment from 'moment'
import partition from 'lodash/partition'
import variables from 'styles/_variables.scss'
import { formatProposalCardDealStatus } from './helpers'

export const fromConstantPropertyToSelectOptions = (arr, property) =>
  arr.map(item => ({
    value: item,
    label: APP_CONSTANTS[property][item]
  }))

export const fromConstantPropertyToSelectValue = (value, property) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'object' && value !== null) return value
  return {
    value,
    label: APP_CONSTANTS[property][value]
  }
}

export const fromBoardItems = (rowData, withSelected = true) => {
  for (const status in rowData) {
    rowData[status].forEach(item => {
      item.id = String(item.id)
      if (withSelected) item.selected = false
    })
  }

  return rowData
}

export const laneTotalValue = items => {
  return items ? items.reduce((a, b) => a + (b.value || 0), 0) : 0
}

export const toSingleTask = task => {
  const { subject, notes, type, assignee, dueDate, startDate, company, contact } = task
  return {
    subject,
    notes,
    type,
    dueDate,
    startDate,
    userId: assignee ? assignee.value : null,
    companyId: company ? company.value : null,
    contactId: contact ? contact.value : null
  }
}

export const toTask = (task, param = {}, isEdit = false) => {
  return {
    ...param,
    subject: task.subject,
    notes: task.notes,
    type: task.type,
    ...(isEdit ? {} : { proposalIds: task.proposals ? task.proposals.map(p => p.id) : null }),
    dueDate: task.dueDate,
    startDate: task.startDate,
    userId: task.userId ? task.userId : task.assignee ? task.assignee.value : null,
    companyId: task.companyId ? task.companyId : task.company ? task.company.value : null,
    contactId: task.contactId ? task.contactId : task.contact ? task.contact.value : null
  }
}

export const getTaskCategory = task => {
  return !task.dateCompleted && whenIsDate(task.dueDate) > 0
    ? 'late'
    : !task.dateCompleted && moment(task.dueDate).isSame(moment(new Date()), 'day')
      ? 'today'
      : !task.dateCompleted && whenIsDate(task.dueDate) < 0
        ? 'scheduled'
        : task.dateCompleted
          ? 'completed'
          : ''
}

export const fromTask = task => {
  const {
    subject,
    type,
    proposalId,
    dueDate,
    startDate,
    notes,
    userId,
    userName,
    id,
    dealId,
    dealName,
    dateCompleted,
    companyId,
    companyName,
    companyNoBid,
    contactId,
    contactName,
    createdBy
  } = task

  const category = getTaskCategory(task)

  return {
    id,
    dealId,
    dealName,
    subject,
    type,
    category,
    createdBy,
    proposals: proposalId ? [proposalId] : [],
    dueDate: moment(dueDate).toDate(),
    startDate: moment(startDate).toDate(),
    dateCompleted,
    completed: !!dateCompleted,
    notes,
    assignee: {
      value: userId,
      label: userName
    },
    company: companyId
      ? {
          value: companyId,
          label: companyName,
          color: getNoBidClientStyle(companyNoBid).color
        }
      : '',
    contact: contactId
      ? {
          value: contactId,
          label: contactName
        }
      : '',
    contacts: []
  }
}

export const tasksObjMapper = tasks => ({
  late: tasks.filter(task => !task.dateCompleted && whenIsDate(task.dueDate) > 0),
  today: tasks.filter(
    task => !task.dateCompleted && moment(task.dueDate).isSame(moment(new Date()), 'day')
  ),
  scheduled: tasks.filter(task => !task.dateCompleted && whenIsDate(task.dueDate) < 0),
  completed: tasks.filter(task => task.dateCompleted)
})

export const dealTasksMapper = (tasks, deal) => {
  const [dealTasks, proposalsTasks] = partition(tasks, task => task.proposalId === null)
  const tasksGroupedByProposalName = groupBy(proposalsTasks, 'proposalName')
  const tasksGroupedByProposalNameMap = []
  for (const proposal in tasksGroupedByProposalName) {
    const tasks = tasksGroupedByProposalName[proposal]
    tasksGroupedByProposalNameMap.push({
      sectionName: `Proposal Tasks: ${proposal}`,
      tasks: tasksObjMapper(tasks)
    })
  }
  if (!tasks.length) return []
  return [
    {
      sectionName: `Opportunity Tasks: ${deal}`,
      tasks: tasksObjMapper(dealTasks)
    },
    ...tasksGroupedByProposalNameMap
  ]
}

export const proposalTasksMapper = (tasks, proposal) => {
  if (!tasks.length) return []
  return [
    {
      sectionName: `Proposal Tasks: ${proposal}`,
      tasks: tasksObjMapper(tasks)
    }
  ]
}

const getUserName = (users, userId) => {
  const user = users.find(u => u.id === userId)
  return user ? `${user.firstName} ${user.lastName}` : ''
}

export const toProposalsListData = (users, proposals) => {
  return proposals.length
    ? proposals.map(proposal => ({
        ...proposal,
        createdBy: {
          id: proposal.createdBy,
          name: getUserName(users, proposal.createdBy)
        },
        updatedBy: {
          id: proposal.updatedBy,
          name: getUserName(users, proposal.updatedBy)
        }
      }))
    : []
}

export const toProposalsBoardData = (proposals, dealStatuses) => {
  return {
    lanes: [
      {
        id: 'CREATION',
        title: APP_CONSTANTS.ProposalStatus.CREATION,
        subTitle: formatProposalCardDealStatus(dealStatuses, 'BIDLIST'),
        cards: proposals.CREATION || [],
        style: { borderTop: `4px solid ${variables.boardColumnRed}` }
      },
      {
        id: 'CHANGES',
        title: APP_CONSTANTS.ProposalStatus.CHANGES,
        subTitle: formatProposalCardDealStatus(dealStatuses, 'BIDLIST'),
        cards: proposals.CHANGES || [],
        style: { borderTop: `4px solid ${variables.boardColumnRed}` }
      },
      {
        id: 'INTERNALREVIEW',
        title: APP_CONSTANTS.ProposalStatus.INTERNALREVIEW,
        subTitle: formatProposalCardDealStatus(dealStatuses, 'REVIEWESTIMATE'),
        cards: proposals.INTERNALREVIEW || [],
        style: { borderTop: `4px solid ${variables.boardColumnRed}` }
      },
      {
        id: 'READYTOSEND',
        title: APP_CONSTANTS.ProposalStatus.READYTOSEND,
        subTitle: formatProposalCardDealStatus(dealStatuses, 'APPROVEDESTIMATE'),
        cards: proposals.READYTOSEND || [],
        style: { borderTop: `4px solid ${variables.boardColumnRed}` }
      },
      {
        id: 'SENT',
        title: APP_CONSTANTS.ProposalStatus.SENT,
        subTitle: 'Opportunity status = Follow Up System Category',
        subtitleTooltip: `If client is awarded the opportunity, opportunity will automatically move into "${formatProposalCardDealStatus(
          dealStatuses,
          'CONTRACTORAWARDED',
          false
        )}"  Stage, otherwise it will move into "${formatProposalCardDealStatus(
          dealStatuses,
          'FOLLOWUP',
          false
        )}", from there the opportunity can be moved into any other follow up category statues from the opportunity board.`,
        cards: proposals.SENT || [],
        style: { borderTop: `4px solid ${variables.boardColumnYellow}` }
      },
      {
        id: 'RECEIVED',
        title: APP_CONSTANTS.ProposalStatus.RECEIVED,
        subTitle: 'Opportunity status = Follow Up System Category',
        subtitleTooltip: `If client is awarded the opportunity, opportunity will automatically move into "${formatProposalCardDealStatus(
          dealStatuses,
          'CONTRACTORAWARDED',
          false
        )}"  Stage, otherwise it will move into "${formatProposalCardDealStatus(
          dealStatuses,
          'FOLLOWUP',
          false
        )}", from there the opportunity can be moved into any other follow up category statues from the opportunity board.`,
        cards: proposals.RECEIVED || [],
        style: { borderTop: `4px solid ${variables.boardColumnYellow}` }
      },
      {
        id: 'YY',
        title: APP_CONSTANTS.ProposalStatus.YY,
        subTitle: formatProposalCardDealStatus(dealStatuses, 'YY'),
        cards: proposals.YY || [],
        style: { borderTop: `4px solid ${variables.boardColumnGreen}` }
      },
      {
        id: 'YN',
        title: APP_CONSTANTS.ProposalStatus.YN,
        subTitle: formatProposalCardDealStatus(dealStatuses, 'YN'),
        cards: proposals.YN || [],
        style: { borderTop: `4px solid ${variables.boardColumnGreen}` }
      },
      {
        id: 'NN',
        title: APP_CONSTANTS.ProposalStatus.NN,
        subTitle: formatProposalCardDealStatus(dealStatuses, 'NN'),
        cards: proposals.NN || [],
        style: { borderTop: `4px solid ${variables.boardColumnGreen}` }
      },
      {
        id: 'NY',
        title: APP_CONSTANTS.ProposalStatus.NY,
        subTitle: formatProposalCardDealStatus(dealStatuses, 'YY'),
        cards: proposals.NY || [],
        style: { borderTop: `4px solid ${variables.boardColumnGreen}` }
      },
      {
        id: 'ARCHIVED',
        title: APP_CONSTANTS.ProposalStatus.ARCHIVED,
        cards: proposals.ARCHIVED || [],
        style: { borderTop: `4px solid ${variables.boardColumnBlack}` }
      }
    ]
  }
}

export const fromCompanies = companies => {
  if (!companies?.length) return []
  if (companies[0].value) return companies

  return companies.map(company => {
    const c = company.company || company
    return {
      value: c.id,
      label: c.name,
      color: getNoBidClientStyle(c.noBid).color
    }
  })
}

export const fromUsers = users => {
  return users.map(user => ({
    label: user.firstName + ' ' + user.lastName,
    value: user.id
  }))
}

export const fromAwardedCompanies = companies => {
  if (!companies) return null
  if (Array.isArray(companies)) {
    return companies.map(company => ({
      value: company.id,
      label: company.name
    }))
  } else {
    return {
      value: companies.id,
      label: companies.name
    }
  }
}

export const toCompanyClients = companies => {
  return companies.map(c => {
    return {
      dueDate: c.dueDate,
      followUpDate: c.followUpDate,
      label: c.company.name,
      value: c.company.id,
      selectedContacts: c.dealContacts.map(c => ({
        isMain: c.isMain,
        value: c.contact.id,
        label: c.contact.firstName + ' ' + c.contact.lastName,
        email: c.contact.email,
        phone: c.contact.phone,
        role: c.role,
        firstName: c.contact.firstName,
        lastName: c.contact.lastName,
        id: c.contact.id
      })),
      color: getNoBidClientStyle(c.company.noBid).color,
      invitedToBid: c.invitedToBid,
      listedWithThisClient: c.listedWithThisClient
    }
  })
}

export const toDealStatuses = statuses =>
  statuses.map(status => ({
    value: status.code,
    label: status.name,
    categoryCode: status.category?.code
  }))

export const toDealStatusesMapper = statuses => {
  return statuses && statuses.length
    ? statuses.reduce((a, status) => {
        if (!a[status.code]) {
          a[status.code] = { name: status.name, group: status.group, category: status.category }
        }
        return a
      }, {})
    : null
}

export const toTeams = teams =>
  teams
    ? teams.map(team => ({
        value: team.id,
        label: team.name + ' - ' + team.abbreviation,
        abbreviation: team.abbreviation,
        count: team.usersCount
      }))
    : []

export const toTemplates = templates =>
  templates.map(template => {
    const { id, title, pageType, content, createdBy, createdAt, lastModifiedBy, lastModifiedOn } =
      template
    return {
      id,
      name: title,
      pageType: pageType.toLowerCase(),
      content,
      createdAt,
      createdBy,
      lastModifiedOn,
      lastModifiedBy
    }
  })
